import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { AppShell } from "./layout/AppShell.js";
import { consumePostLogoutTarget } from "./layout/postLogoutTarget.js";
import { ModuleRouteGuard } from "./modules/ModuleRouteGuard.js";
// Sprint 55.2 — `/accept-invite` is served by PublicAuthRouter
// (see apps/web/src/auth/PublicAuthRouter.tsx). It must NOT be
// imported here; routing it through the authenticated tree
// re-introduces the SessionProvider redirect that originally
// bounced unauthenticated recipients to Keycloak login.

/**
 * Web-app router. All pages render inside `AppShell`, which owns
 * the sticky header, navigation, and max-width content wrapper —
 * per docs/foundation/DESIGN_SYSTEM_RULES.md §2 (single shell,
 * no per-page chrome drift).
 *
 * Sprint 19 Wave I — code-splitting. Heavy non-critical pages are
 * lazy-loaded so the initial bundle only ships the always-on shell
 * (Sprint 60 collapsed the old `HomePage` cards route into a
 * `Navigate to="/dashboard" replace` so the operator lands on the
 * fleet-operations page directly). Pages a dispatcher hits in the first second of
 * a session (Map, Insights) stay eager. Pages with
 * heavier deps (charts, tables, map polylines, drawer plumbing)
 * are deferred and wrapped in a single `<Suspense>` boundary so the
 * AppShell continues rendering while the chunk arrives.
 */
// E-BUNDLE-9 — die letzten beiden eager Routen. Weder /geofences noch
// /intelligence liegt im ersten Paint: die Startroute "/" leitet auf
// /dashboard um, das selbst schon ein eigener Chunk ist. Sichtbar wird
// hier nur der Suspense-Fallback beim ersten Aufruf dieser Routen.
// GT-GFI-AI-COMMAND-CENTER-UX-FOUNDATION-01 — the flagship AI home that brings
// Ask Genius + AI Reports + status together (reuses the existing surfaces).
const GeofencesPage = lazy(() =>
  import("./geofences/GeofencesPage.js").then((m) => ({ default: m.GeofencesPage })),
);
const CommandCenterPage = lazy(() =>
  import("./intelligence/CommandCenterPage.js").then((m) => ({ default: m.CommandCenterPage })),
);
const VehiclesPage = lazy(() =>
  import("./vehicles/VehiclesPage.js").then((m) => ({ default: m.VehiclesPage })),
);
const VehicleDetailPage = lazy(() =>
  import("./vehicles/VehicleDetailPage.js").then((m) => ({ default: m.VehicleDetailPage })),
);
const VehicleCreatePage = lazy(() =>
  import("./vehicles/VehicleFormPage.js").then((m) => ({ default: m.VehicleCreatePage })),
);
const VehicleEditPage = lazy(() =>
  import("./vehicles/VehicleFormPage.js").then((m) => ({ default: m.VehicleEditPage })),
);
const DashboardPage = lazy(() =>
  import("./dashboard/DashboardPage.js").then((m) => ({ default: m.DashboardPage })),
);
const TripsPage = lazy(() =>
  import("./trips/TripsPage.js").then((m) => ({ default: m.TripsPage })),
);
const TripDetailPage = lazy(() =>
  import("./trips/TripDetailPage.js").then((m) => ({ default: m.TripDetailPage })),
);
const DevicesPage = lazy(() =>
  import("./devices/DevicesPage.js").then((m) => ({ default: m.DevicesPage })),
);
const OfflineDevicesPage = lazy(() =>
  import("./devices/OfflineDevicesPage.js").then((m) => ({ default: m.OfflineDevicesPage })),
);
const AlertsPage = lazy(() =>
  import("./alerts/AlertsPage.js").then((m) => ({ default: m.AlertsPage })),
);
const ImportsPage = lazy(() =>
  import("./imports/ImportsPage.js").then((m) => ({ default: m.ImportsPage })),
);
const ImportDetailPage = lazy(() =>
  import("./imports/ImportDetailPage.js").then((m) => ({ default: m.ImportDetailPage })),
);
const GroupsPage = lazy(() =>
  import("./groups/GroupsPage.js").then((m) => ({ default: m.GroupsPage })),
);
const GroupDetailPage = lazy(() =>
  import("./groups/GroupDetailPage.js").then((m) => ({ default: m.GroupDetailPage })),
);
const ReportsPage = lazy(() =>
  import("./reports/ReportsPage.js").then((m) => ({ default: m.ReportsPage })),
);
const ExportHistoryPage = lazy(() =>
  import("./reports-history/ExportHistoryPage.js").then((m) => ({
    default: m.ExportHistoryPage,
  })),
);
const DemoScenariosPage = lazy(() =>
  import("./demo/DemoScenariosPage.js").then((m) => ({ default: m.DemoScenariosPage })),
);
// Sprint 45 (GT-MEGA-FLEET-OPS-UI-COMPLETE-45) — Fleet Ops UI built on
// top of Sprint 44 backend foundations.
const MaintenancePage = lazy(() =>
  import("./maintenance/MaintenancePage.js").then((m) => ({ default: m.MaintenancePage })),
);
const CalendarPage = lazy(() =>
  import("./calendar/CalendarPage.js").then((m) => ({ default: m.CalendarPage })),
);
// Sprint 48.11 (theme 25 — Fuel / Cost / ESG Operations) — read-only
// list view of derived fuel events (refuel / suspected_loss /
// level_drop / unknown). Backend GET /v1/fuel/events shipped in
// Sprint 39 with no UI surface until now.
const FuelEventsPage = lazy(() =>
  import("./fuel/FuelEventsPage.js").then((m) => ({ default: m.FuelEventsPage })),
);
// A-FUEL-SUSPICIOUS-UI1 (DL-197 §5/§7) — the `Suspicious fuel` review
// queue. A case is an observation opened by a rule; its classification is
// a human decision. The review-case endpoints are on main since
// D-FUEL-REVIEW-CASES1, so the nav entry is a real link now — the "Soon"
// badge came off together with the data behind it, not before.
const FuelReviewCasesPage = lazy(() =>
  import("./fuel/FuelReviewCasesPage.js").then((m) => ({ default: m.FuelReviewCasesPage })),
);
// GT-NAV-IA-DOMAIN-GROUPING-FUEL-COSTS-GENIUS-FAMILY-01 — Genius Automations
// (approved future product family) Coming Soon page. Presentation-only, no backend.
const GeniusAutomationsPage = lazy(() =>
  import("./genius/GeniusAutomationsPage.js").then((m) => ({
    default: m.GeniusAutomationsPage,
  })),
);
// Paket 003702 — Genius Reports Coming Soon page (NOT the Genius Intelligence
// AI Reports section). Presentation-only, no backend.
const GeniusReportsPage = lazy(() =>
  import("./genius/GeniusReportsPage.js").then((m) => ({
    default: m.GeniusReportsPage,
  })),
);
// Fuel Cost / ESG dashboard. E-KOSTENSTELLE-WEB1 (DL-259) moved the read
// to `/v1/fuel/costs/cost-center`, the ONE cost centre over
// `fueling_transactions`; per-row links into VehicleDetailPage /
// DriverDetailPage / FuelEventsPage.
const FuelCostsPage = lazy(() =>
  import("./fuel/FuelCostsPage.js").then((m) => ({ default: m.FuelCostsPage })),
);
const FuelConsumptionPage = lazy(() =>
  import("./fuel/FuelConsumptionPage.js").then((m) => ({ default: m.FuelConsumptionPage })),
);
// GT-FUEL-SOURCE-NEUTRAL-CSV-XLSX-FUELING-IMPORT-BUILD-01 (GT-OPEN-120)
// — the CSV/XLSX fueling import flow. A SUB-ROUTE of Fuel costs, reached
// from that page; it deliberately adds no main-navigation item and does
// not touch the six locked Fuel labels.
const FuelImportPage = lazy(() =>
  import("./fuel/FuelImportPage.js").then((m) => ({ default: m.FuelImportPage })),
);
// Sprint 49.2 Batch C (theme 13) — SLA policy settings page.
// Tenant-admin-only writes; reads gated by vehicle.read at the route.
const AlertSlaSettingsPage = lazy(() =>
  import("./alerts/AlertSlaSettingsPage.js").then((m) => ({ default: m.AlertSlaSettingsPage })),
);
// F-FUEL-DATEV-SETTINGS1 — per-tenant DATEV export settings page.
// Tenant-admin-only writes; closes the gap left by F-FUEL-COST-REPORTS1
// (PR #2511) where the datev_booking_batch report had no settings UI.
const FuelDatevSettingsPage = lazy(() =>
  import("./fuel/FuelDatevSettingsPage.js").then((m) => ({ default: m.FuelDatevSettingsPage })),
);
// E-FUEL-BUDGET-EINGABE1 — monthly per-vehicle fuel budgets. `fuel_budgets`
// has been READ by the budget_variance report and the fuel notify job since
// migration 0166, but nothing wrote a row; this is the missing write surface.
const FuelBudgetsPage = lazy(() =>
  import("./fuel/FuelBudgetsPage.js").then((m) => ({ default: m.FuelBudgetsPage })),
);
// Sprint 49.3 Batch D (theme 7) — recurring maintenance rules page.
// Operator-driven materialization (no cron / scheduler).
const MaintenanceRecurringPage = lazy(() =>
  import("./maintenance/MaintenanceRecurringPage.js").then((m) => ({
    default: m.MaintenanceRecurringPage,
  })),
);
// B-E4-6 (E4e) - tenant-wide maintenance template catalog. Reads are
// open to every operator; writes and "apply to N vehicles" are admin.
const MaintenanceTemplatesPage = lazy(() =>
  import("./maintenance/MaintenanceTemplatesPage.js").then((m) => ({
    default: m.MaintenanceTemplatesPage,
  })),
);
// Sprint 49.6 Batch G (theme 15) — unified lifecycle / archived
// overview. Read-only aggregator + per-entity restore dispatch.
const LifecycleArchivedPage = lazy(() =>
  import("./admin/LifecycleArchivedPage.js").then((m) => ({
    default: m.LifecycleArchivedPage,
  })),
);
const RulesRegistryPage = lazy(() =>
  import("./rules-registry/RulesRegistryPage.js").then((m) => ({
    default: m.RulesRegistryPage,
  })),
);
const RbacPage = lazy(() => import("./rbac/RbacPage.js").then((m) => ({ default: m.RbacPage })));
const SupportReportPage = lazy(() =>
  import("./support/SupportReportPage.js").then((m) => ({ default: m.SupportReportPage })),
);
// Sprint 21 Wave C (Phase 1B) — onboarding wizard.
const OnboardingPage = lazy(() =>
  import("./onboarding/OnboardingPage.js").then((m) => ({ default: m.OnboardingPage })),
);
// Sprint 21 Wave B (Phase 1B) — tenant settings.
const SettingsPage = lazy(() =>
  import("./settings/SettingsPage.js").then((m) => ({ default: m.SettingsPage })),
);
// D-MODUL-SEITE1 (PASS 001693) — "Module und Paket", admin/owner only.
const ModulesPage = lazy(() =>
  import("./settings/ModulesPage.js").then((m) => ({ default: m.ModulesPage })),
);
// Sprint 21 Wave B — Drivers MVP.
const DriversPage = lazy(() =>
  import("./drivers/DriversPage.js").then((m) => ({ default: m.DriversPage })),
);
const DriverCreatePage = lazy(() =>
  import("./drivers/DriversPage.js").then((m) => ({ default: m.DriverCreatePage })),
);
const DriverDetailPage = lazy(() =>
  import("./drivers/DriverDetailPage.js").then((m) => ({ default: m.DriverDetailPage })),
);
// Sprint 21 Wave B (Phase 1D) — Gateway Diagnostics page (real,
// persisted IMEI rejection log).
const GatewayDiagnosticsPage = lazy(() =>
  import("./devices/GatewayDiagnosticsPage.js").then((m) => ({
    default: m.GatewayDiagnosticsPage,
  })),
);
// Sprint 26 Wave H — Driver Field Mode landing page. Gated by
// sprint26.driverFieldMode at the page level.
const DriverFieldModePage = lazy(() =>
  import("./driver/DriverFieldModePage.js").then((m) => ({ default: m.DriverFieldModePage })),
);
// GT-CUSTOMER-NOTIFICATIONS-WEB-INAPP-04 — Notification Center on the
// Sprint 3 backend (`/v1/me/notifications*`). The top-bar bell links
// here; preferences stay at /me/notifications.
const NotificationsPage = lazy(() =>
  import("./notifications/NotificationsPage.js").then((m) => ({ default: m.NotificationsPage })),
);
// E-BUNDLE-6 — deferred detail/admin/self-service pages. These were the
// largest remaining eager modules in the web entry chunk (measured on the
// built entry: AlertDetailPage 31.401 B of source, NotificationPreferencesPage
// 28.420 B, VisibilityPage 16.925 B, TenantBrandingPage 15.927 B, plus the
// smaller rules/schedules/admin/me pages). None of them is a first-second
// navigation target: the SPA lands on /dashboard, so they are reached only
// after an explicit navigation. They move behind the same single <Suspense>
// boundary with the same LoadingState fallback — no new fallback type.
const AlertDetailPage = lazy(() =>
  import("./alerts/AlertDetailPage.js").then((m) => ({ default: m.AlertDetailPage })),
);
const ScheduleRunsPage = lazy(() =>
  import("./reports/schedules/ScheduleRunsPage.js").then((m) => ({
    default: m.ScheduleRunsPage,
  })),
);
const RulesPage = lazy(() =>
  import("./rules/RulesPage.js").then((m) => ({ default: m.RulesPage })),
);
const NotificationPreferencesPage = lazy(() =>
  import("./me/notifications/NotificationPreferencesPage.js").then((m) => ({
    default: m.NotificationPreferencesPage,
  })),
);
// Sprint 55.3 — profile self-service (password + email change).
const ProfilePage = lazy(() =>
  import("./me/ProfilePage.js").then((m) => ({ default: m.ProfilePage })),
);
const AccountDeletionPage = lazy(() =>
  import("./account/AccountDeletionPage.js").then((m) => ({ default: m.AccountDeletionPage })),
);
const AdminHubPage = lazy(() =>
  import("./admin/AdminHubPage.js").then((m) => ({ default: m.AdminHubPage })),
);
const UserGroupsPage = lazy(() =>
  import("./admin/UserGroupsPage.js").then((m) => ({ default: m.UserGroupsPage })),
);
const VisibilityPage = lazy(() =>
  import("./admin/VisibilityPage.js").then((m) => ({ default: m.VisibilityPage })),
);
const TenantBrandingPage = lazy(() =>
  import("./admin/TenantBrandingPage.js").then((m) => ({ default: m.TenantBrandingPage })),
);
const TenantActivityPage = lazy(() =>
  import("./admin/TenantActivityPage.js").then((m) => ({ default: m.TenantActivityPage })),
);

// E-BUNDLE-5 — the five heaviest contributors to the entry chunk. Moving
// them behind React.lazy keeps the shell (AppShell + routing + session)
// in the entry and defers the page code to its own chunk. Same
// `<Suspense>` boundary as every other deferred page; no behaviour change.
const MapPage = lazy(() => import("./map/MapPage.js").then((m) => ({ default: m.MapPage })));
const RuleEditorPage = lazy(() =>
  import("./rules/RuleEditorPage.js").then((m) => ({ default: m.RuleEditorPage })),
);
const SchedulesPage = lazy(() =>
  import("./reports/schedules/SchedulesPage.js").then((m) => ({ default: m.SchedulesPage })),
);
const ScheduleEditorPage = lazy(() =>
  import("./reports/schedules/ScheduleEditorPage.js").then((m) => ({
    default: m.ScheduleEditorPage,
  })),
);
const TenantUsersPage = lazy(() =>
  import("./admin/TenantUsersPage.js").then((m) => ({ default: m.TenantUsersPage })),
);

/**
 * Sprint 22 Wave E (S22-E3) follow-up — Codex P2 from #212 review.
 *
 * The web router declarations live in a single `WEB_ROUTES` array
 * so the route table is the only source of truth. Every `<Route>`
 * the SPA mounts is derived from this array; consumers that need
 * to reason about the registered route set (e.g.
 * `apps/web/src/demo/guidedTourSteps.ts`) import `WEB_ROUTE_PATHS`
 * from here and stay in lock-step with the SPA automatically.
 *
 * The catch-all `path: "*"` entry is intentionally kept inside the
 * array so adding a new route never accidentally falls through to
 * `<NotFound>` — `<Routes>` matches in source order and `*` is
 * always last.
 */
interface WebRouteSpec {
  readonly path: string;
  readonly element: ReactNode;
}

export const WEB_ROUTES: readonly WebRouteSpec[] = [
  { path: "/", element: <Navigate to="/dashboard" replace /> },
  // Sprint 55.2 (Owner UI smoke F-55-106) — `/accept-invite` is
  // served exclusively by `PublicAuthRouter`. It was previously
  // registered here AND in PUBLIC_AUTH_PATH_PREFIXES (the latter
  // omission was the actual bug: main.tsx mounted SessionProvider
  // for unauthenticated recipients, which auto-redirected to
  // Keycloak login before this route was ever reached). Keeping a
  // copy here is dead code at best and risks divergence between
  // the public and authenticated mount points — single source of
  // truth lives in PublicAuthRouter.
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/vehicles", element: <VehiclesPage /> },
  { path: "/vehicles/new", element: <VehicleCreatePage /> },
  { path: "/vehicles/:id", element: <VehicleDetailPage /> },
  { path: "/vehicles/:id/edit", element: <VehicleEditPage /> },
  { path: "/map", element: <MapPage /> },
  { path: "/geofences", element: <GeofencesPage /> },
  { path: "/trips", element: <TripsPage /> },
  { path: "/trips/:id", element: <TripDetailPage /> },
  { path: "/devices", element: <DevicesPage /> },
  { path: "/groups", element: <GroupsPage /> },
  { path: "/groups/:id", element: <GroupDetailPage /> },
  /*
    GT-FLEET-OWNER-SMOKE-STRUCTURAL-RECOVERY-01 — the Devices area is
    four peer views behind one route-aware shell
    (`devices/DevicesShell.tsx`), in the Owner-specified order
    Live | Offline | Archived | Gateway diagnostics.

    `/devices/archived` is new: Archived used to be component-local tab
    state on `/devices`, so it could not be deep-linked and did not
    survive a refresh or browser Back. It renders the same
    `DevicesPage`, which derives its list view from the path. The two
    pre-existing sub-routes keep their exact paths, so every link that
    worked before still works.
  */
  { path: "/devices/archived", element: <DevicesPage /> },
  { path: "/devices/offline", element: <OfflineDevicesPage /> },
  { path: "/intelligence", element: <CommandCenterPage /> },
  /*
    GT-OPEN-154.3 — der alte Genius-Intelligence-Weg `/insights` ist
    geloescht (Owner-Entscheidung 10: "es gibt keine Seite Insights
    mehr"). Der Pfad bleibt als Weiterleitung auf `/intelligence`
    stehen, damit alte Lesezeichen und externe Links nicht ins Leere
    laufen; `replace` haelt den Verlauf sauber (gleiche Form wie `/`
    und `/support`).
  */
  { path: "/insights", element: <Navigate to="/intelligence" replace /> },
  { path: "/alerts", element: <AlertsPage /> },
  { path: "/alerts/:id", element: <AlertDetailPage /> },
  { path: "/reports", element: <ReportsPage /> },
  { path: "/imports", element: <ImportsPage /> },
  { path: "/imports/:id", element: <ImportDetailPage /> },
  { path: "/reports/history", element: <ExportHistoryPage /> },
  { path: "/rules", element: <RulesPage /> },
  { path: "/rules/new", element: <RuleEditorPage /> },
  { path: "/rules/:id", element: <RuleEditorPage /> },
  { path: "/reports/schedules", element: <SchedulesPage /> },
  { path: "/reports/schedules/new", element: <ScheduleEditorPage /> },
  { path: "/reports/schedules/:id", element: <ScheduleEditorPage /> },
  { path: "/reports/schedules/:id/runs", element: <ScheduleRunsPage /> },
  { path: "/demo", element: <DemoScenariosPage /> },
  // Sprint 21 Wave C (Phase 1B) — onboarding wizard.
  { path: "/onboarding", element: <OnboardingPage /> },
  // Sprint 21 Wave B (Phase 1B) — tenant settings.
  { path: "/settings", element: <SettingsPage /> },
  { path: "/settings/modules", element: <ModulesPage /> },
  // Sprint 21 Wave B — Drivers MVP.
  { path: "/drivers", element: <DriversPage /> },
  { path: "/drivers/new", element: <DriverCreatePage /> },
  { path: "/drivers/:id", element: <DriverDetailPage /> },
  // Sprint 21 Wave B (Phase 1D) — Gateway diagnostics.
  { path: "/devices/diagnostics", element: <GatewayDiagnosticsPage /> },
  // GT-CUSTOMER-NOTIFICATIONS-WEB-INAPP-04 — the Notification Center
  // (in-app inbox). The top-bar bell badge links here.
  { path: "/notifications", element: <NotificationsPage /> },
  // Sprint 19 Wave D — account-scoped page; intentionally not in
  // the primary nav (see AppShell). Reachable from /me/notifications.
  { path: "/me/notifications", element: <NotificationPreferencesPage /> },
  // Sprint 55.3 — profile self-service (password + email change).
  // Reached from the avatar dropdown in the top bar.
  { path: "/me/profile", element: <ProfilePage /> },
  // G-WEB-DELETE1 — also registered in the public router so the stable
  // app-store deletion URL works with or without an active session. This
  // authenticated registration keeps client-side navigation from the account
  // menu inside the SPA instead of falling through to NotFound.
  { path: "/account/delete", element: <AccountDeletionPage /> },
  // Sprint 26 Wave H — Driver Field Mode landing page. Gated by
  // sprint26.driverFieldMode + the `driver` role inside the page.
  { path: "/driver", element: <DriverFieldModePage /> },
  // Sprint 29.5B finding #2 — Admin Hub (customer-side launchpad).
  // The bare `/admin` is owned by the separate Admin SPA via Caddy
  // 308 redirect, so the customer-side hub lives at `/admin-hub`.
  // Visibility is role-gated at render time inside AdminHubPage
  // (defence in depth on top of the AppShell pill-nav gate).
  { path: "/admin-hub", element: <AdminHubPage /> },
  // Sprint 33 (GT-TENANT-ADMIN-COMPLETE-02) — tenant-side admin
  // sub-pages. Both pages role-gate themselves on tenant.admin /
  // tenant.owner internally.
  { path: "/admin-hub/users", element: <TenantUsersPage /> },
  { path: "/admin-hub/user-groups", element: <UserGroupsPage /> },
  { path: "/admin-hub/visibility", element: <VisibilityPage /> },
  { path: "/admin-hub/branding", element: <TenantBrandingPage /> },
  // Sprint 52.0 (Main App Tenant Admin Hub Completion) — tenant
  // admin activity feed. Tenant-scoped audit subset; no platform
  // actions. Internally gated on tenant.admin / tenant.owner.
  { path: "/admin-hub/activity", element: <TenantActivityPage /> },
  // Sprint 45 (GT-MEGA-FLEET-OPS-UI-COMPLETE-45) — Fleet Ops UI.
  { path: "/maintenance", element: <MaintenancePage /> },
  { path: "/calendar", element: <CalendarPage /> },
  // Sprint 48.11 (theme 25) — read-only fuel events list.
  { path: "/fuel/events", element: <FuelEventsPage /> },
  // Sprint 49.1 (Batch B — theme 25 closure) — Fuel Cost / ESG dashboard.
  { path: "/fuel/costs", element: <FuelCostsPage /> },
  // The consumption card, its hooks and its endpoint all shipped earlier; the
  // nav entry was a "Soon" preview with no route behind it, so a finished
  // feature looked unbuilt. This is the missing wiring, not a new feature.
  { path: "/fuel/consumption", element: <FuelConsumptionPage /> },
  // A-FUEL-SUSPICIOUS-UI1 — `Suspicious fuel` review queue (DL-197 §5/§7).
  { path: "/fuel/review-cases", element: <FuelReviewCasesPage /> },
  // GT-FUEL-SOURCE-NEUTRAL-CSV-XLSX-FUELING-IMPORT-BUILD-01 (GT-OPEN-120)
  // — `Import fuelings`, entered from Fuel costs. No new menu item.
  { path: "/fuel/costs/import", element: <FuelImportPage /> },
  // Sprint 49.2 Batch C (theme 13) — SLA policy settings.
  { path: "/alerts/sla", element: <AlertSlaSettingsPage /> },
  { path: "/fuel/datev-settings", element: <FuelDatevSettingsPage /> },
  // E-FUEL-BUDGET-EINGABE1 — entered from the budget_variance report's empty
  // state, like /fuel/datev-settings above. No new menu item.
  { path: "/fuel/budgets", element: <FuelBudgetsPage /> },
  // Sprint 49.3 Batch D (theme 7) — recurring maintenance rules.
  { path: "/maintenance/recurring", element: <MaintenanceRecurringPage /> },
  { path: "/maintenance/templates", element: <MaintenanceTemplatesPage /> },
  // Sprint 49.6 Batch G (theme 15) — unified lifecycle / archived overview.
  // Customer-app tenant-admin surface. `/admin/...` is reserved for the
  // separate Platform Admin Console; customer-side admin-hub routes
  // live under `/admin-hub/...`.
  { path: "/admin-hub/archived", element: <LifecycleArchivedPage /> },
  { path: "/rules-registry", element: <RulesRegistryPage /> },
  { path: "/admin-hub/roles", element: <RbacPage /> },
  { path: "/support/report", element: <SupportReportPage /> },
  // Sprint 47.0 — `/support` (no `/report` suffix) used to 404. Add a
  // redirect so a user typing the natural URL lands on the report
  // form. The original `/support/report` path is kept for back-compat
  // with deep-links from the Sprint 45 closeout.
  { path: "/support", element: <Navigate to="/support/report" replace /> },
  // GT-NAV-IA-DOMAIN-GROUPING-FUEL-COSTS-GENIUS-FAMILY-01 — Genius Automations
  // (future product family) Coming Soon preview. Honest, polished, no backend.
  { path: "/genius/automations", element: <GeniusAutomationsPage /> },
  { path: "/genius/reports", element: <GeniusReportsPage /> },
  { path: "*", element: <NotFound /> },
];

/**
 * Set of every concrete (non-wildcard) path the SPA registers.
 * Used by `apps/web/src/demo/guidedTourSteps.ts` to filter the
 * Sprint 21 demo step list at runtime so the tour never points at
 * the catch-all 404. Driven directly by `WEB_ROUTES` so adding or
 * removing a route updates this set automatically.
 */
export const WEB_ROUTE_PATHS: ReadonlySet<string> = new Set(
  WEB_ROUTES.map((r) => r.path).filter((p) => p !== "*"),
);

export function App() {
  const { t } = useLocale();
  const { pathname } = useLocation();
  // Sprint 25 Wave A PR-A1 — bounce-back from Admin Console logout.
  // After the operator signs out from `/admin/`, Keycloak's end-
  // session redirect lands on `/` (the customer-app origin, the
  // only path allow-listed in the staging KC realm). The Admin
  // Console set a single-use `localStorage` marker before the KC
  // redirect; if present, we re-redirect to `/admin/` so the
  // operator stays in the Admin Console login flow. See
  // `layout/postLogoutTarget.ts` for the validation contract.
  useEffect(() => {
    const target = consumePostLogoutTarget();
    if (target) {
      window.location.assign(target);
    }
  }, []);
  // Sprint 20.1 PR A — single <Routes> block inside a single <Suspense>
  // boundary. The previous tree rendered two adjacent <Routes> blocks
  // (one eager, one suspended) which caused every URL to match in both
  // and every page to render twice (P0 #1 from staging smoke).
  return (
    <AppShell>
      {/* D-WEB-ROUTE-BOUNDARY1 — a render throw (or a rejected lazy chunk) in
          one route stays inside the shell; the key resets it on navigation.
          The root boundary in main.tsx remains the net for shell/providers. */}
      <ErrorBoundary key={pathname} t={t}>
        <Suspense fallback={<LoadingState label={t("common.loading")} />}>
          <Routes>
            {/* A-MODUL-NAV1 — EIN Routenwaechter fuer alle modulgebundenen
                Routen. Die Zuordnung Route -> Modul steht in
                `modules/moduleMap.ts`; Basis-Routen laufen unverzoegert
                durch. */}
            {WEB_ROUTES.map((r) => (
              <Route
                key={r.path}
                path={r.path}
                element={<ModuleRouteGuard routePath={r.path}>{r.element}</ModuleRouteGuard>}
              />
            ))}
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </AppShell>
  );
}

function NotFound() {
  const { t } = useLocale();
  return (
    <Stack gap={3} align="center" style={{ paddingBlock: 80, textAlign: "center" }}>
      <strong style={{ fontSize: 20 }}>{t("app.notfound.title")}</strong>
      <p style={{ color: "var(--gt-sys-text-muted)", maxInlineSize: 420 }}>
        {t("app.notfound.body")}
      </p>
      <Link
        to="/"
        style={{
          color: "var(--gt-sys-accent)",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        ← {t("common.back")}
      </Link>
    </Stack>
  );
}
